import { friendlyError } from '../../utils/userMessages';
import { resolveCompanyId, getCompanyCoverUrl, getCompanyLogoUrl, getPersonPhotoUrl } from '../../utils/companyIdentity';
import { brandPalette as palette, getReadableColor, getContrastText, getGradientContrastText } from '../../utils/brandPalette';
// src/components/dashboard/ScheduleMeeting.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Schedule Meeting
//  Updated: Only show connected users and companies
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Divider,
    Skeleton,
    alpha,
    Card,
    CardContent,
    CardActions,
    Fade,
    Dialog,
    Tab,
    Tabs,
    Badge,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    Switch,
    FormControlLabel,
    Collapse,
    Stack,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    Event as EventIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    Schedule as ScheduleIcon,
    Send as SendIcon,
    Close as CloseIcon,
    CalendarToday as CalendarIcon,
    Schedule as TimeIcon,
    People as PeopleIcon,
    VideoCall as VideoCallIcon,
    Check as CheckIcon,
    Verified as VerifiedIcon,
    Stars as StarsIcon,
    Search as SearchIcon,
    Phone as PhoneIcon,
    Public as PublicIcon,
    ArrowForward as KeyboardArrowRightIcon,
    AutoAwesome as AiIcon,
    Handshake as HandshakeIcon,
    NetworkCheck as WifiIcon,
    NetworkCheck as CellTowerIcon,
    NetworkCheck as RouterIcon,
    Speed as SpeedIcon,
    SettingsApplications as HubIcon,
    Cloud as CloudIcon,
    NetworkCheck as SignalBarsIcon,
    GraphicEq as SignalIcon,
    Dashboard as GridViewIcon,
    NotesOutlined as ListIcon,
    Person as PersonIcon,
    PersonAdd as PersonAddIcon,
    Group as GroupIcon,
    Handshake as ConnectIcon,
    Chat as ChatIcon,
    Pending as PendingIcon,
    Block as BlockIcon,
    Email as EmailIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Info as InfoIcon,
    Inventory2 as CategoryIcon,
    ShoppingBagRounded as SellIcon,
    ShoppingBagRounded as ShoppingCartIcon,
    TrendingUp as TrendingUpIcon,
    NetworkCheck as NetworkWifiIcon,
    BadgeOutlined as WorkIcon,
    LinkedIn as LinkedInIcon,
    NetworkCheck as WifiTetheringIcon,
    Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    query,
    where,
    addDoc,
    serverTimestamp,
    onSnapshot,
    limit,
    orderBy,
    startAfter,
    doc,
    updateDoc,
    getDoc,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import Header from '../common/Header/Header';
import { getIntegrationSettings } from '../../services/integrations/backend';
import { emailService, smsService } from '../../services/integrations/messaging';
import { generateMeetingRequest } from '../../services/integrations/ai';
import { introductionService, meetingWorkflowService } from '../../services/workflow/communication';
import { getBusyTimes } from '../../services/integrations/calendar';

// ============================================
// DESIGN TOKENS
// ============================================



const ACCENTS = [palette.rust, palette.pine, palette.teal, palette.gold, palette.purple, palette.navy];

// ============================================
// GLASS STYLES
// ============================================

const glassCard = ({ level = 2, radius = '14px', tint = palette.paper, accent = null } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: radius,
    background: '#FFFFFF',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.075)}`,
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : level === 0
            ? '0 2px 7px rgba(25,57,78,.035)'
            : '0 5px 18px rgba(25,57,78,.055)',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
});

const glassLift = (level = 3, accent = palette.rust) => ({
    transform: 'translateY(-2px)',
    borderColor: alpha(accent, 0.14),
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : '0 8px 20px rgba(25,57,78,.07)',
});

const glassWell = (radius = '10px', accent = null) => ({
    borderRadius: radius,
    background: accent ? alpha(accent, 0.045) : '#F4F8FB',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.065)}`,
    boxShadow: 'none',
});

const glassPod = (color, size = 38, radius = '10px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color,
    background: alpha(color, .07),
    border: `1px solid ${alpha(color, .10)}`,
    boxShadow: 'none',
});

// ============================================
// FIX: Update glassPill to handle undefined colors
// ============================================

const glassPill = (color, filled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    minHeight: 22,
    borderRadius: '999px',
    fontSize: '10.5px',
    lineHeight: 1,
    fontWeight: 650,
    whiteSpace: 'nowrap',
    color: filled ? getContrastText(color) : getReadableColor(color),
    background: filled ? color : alpha(color, .07),
    border: `1px solid ${filled ? alpha(color, .12) : alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassButton = (color, deep) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    color: getGradientContrastText(color, deep || color),
    minHeight: 34,
    paddingInline: 12,
    background: `linear-gradient(135deg, ${color}, ${deep || color})`,
    border: `1px solid ${alpha(color, .12)}`,
    boxShadow: `0 5px 14px ${alpha(color, .16)}`,
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 8px 18px ${alpha(color, .20)}` },
    '&.Mui-disabled': { opacity: .58, color: getGradientContrastText(color, deep || color) },
});

const glassGhostButton = (color) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    minHeight: 34,
    color,
    background: 'rgba(255,253,250,.92)',
    border: `1px solid ${alpha(color, .15)}`,
    boxShadow: '0 2px 7px rgba(25,57,78,.03)',
    transition: 'background .18s ease, border-color .18s ease',
    '&:hover': { background: alpha(color, .035), borderColor: alpha(color, .22) },
});

// ============================================
// ANIMATED BACKGROUND
// ============================================

const EnhancedAnimatedBackground = () => (
    <Box
        aria-hidden="true"
        sx={{
            position: 'fixed',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0,
            background: `
                radial-gradient(circle at 6% 2%, rgba(var(--primary-rgb), .085), transparent 30rem),
                radial-gradient(circle at 95% 18%, rgba(var(--secondary-rgb), .065), transparent 32rem),
                linear-gradient(180deg, #F1F6F9 0%, #F1F6F9 48%, #EAF2F6 100%)
            `,
            '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                opacity: .32,
                backgroundImage: `linear-gradient(rgba(31,62,82,.014) 1px, transparent 1px), linear-gradient(90deg, rgba(31,62,82,.014) 1px, transparent 1px)`,
                backgroundSize: '36px 36px',
                maskImage: 'linear-gradient(to bottom, rgba(0,0,0,.72), transparent 88%)',
            },
        }}
    />
);

const SectionRule = ({ color = palette.rust, width = 40 }) => (
    <Box sx={{ width, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${color}, ${color}40, transparent)`, mt: 0.75 }} />
);

// ============================================
// GLASS STEP INDICATOR
// ============================================

const GlassStepIndicator = ({ activeStep, totalSteps }) => (
    <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        mb: 4,
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '10%',
            right: '10%',
            height: '2px',
            background: `linear-gradient(90deg, ${alpha(palette.rust, 0.08)}, ${palette.hairline}, ${alpha(palette.rust, 0.08)})`,
            transform: 'translateY(-50%)',
            zIndex: 0,
        }
    }}>
        {Array.from({ length: totalSteps }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', zIndex: 1 }}>
                <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                >
                    <Box sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                        transition: 'all 0.3s ease',
                        ...glassPod(
                            index < activeStep ? palette.pine :
                            index === activeStep ? palette.rust :
                            palette.inkFaint,
                            index === activeStep ? 48 : 44,
                            '50%'
                        ),
                        transform: index === activeStep ? 'scale(1.08)' : 'scale(1)',
                        boxShadow: index === activeStep ? `0 8px 32px ${alpha(palette.rust, 0.30)}` : index < activeStep ? `0 4px 16px ${alpha(palette.pine, 0.15)}` : '0 2px 8px rgba(25,57,78,0.04)',
                    }}>
                        {index < activeStep ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
                    </Box>
                </motion.div>
                {index < totalSteps - 1 && (
                    <Box sx={{
                        width: 40,
                        height: 2,
                        borderRadius: 1,
                        background: index < activeStep ?
                            `linear-gradient(90deg, ${palette.pine}, ${alpha(palette.pine, 0.3)})` :
                            palette.hairline,
                        transition: 'background-color 0.3s ease',
                        mx: 1,
                    }} />
                )}
            </Box>
        ))}
    </Box>
);

// ============================================
// GLASS GRID COMPANY CARD
// ============================================

const scheduleAccessProgress = (status) => ({
    calendar_shared: 100,
    chat_enabled: 78,
    engaged: 62,
    connected: 55,
    pending: 34,
    declined: 12,
}[status] || 20);

const ScheduleSelectionShell = ({ accentColor, coverImage, fallbackIcon, selected, children, onClick }) => (
    <Card
        elevation={0}
        onClick={onClick}
        className="tf-color-card tf-company-directory-card tf-premium-company-card tf-company-design-card tf-schedule-directory-card"
        sx={{
            '--card-color': accentColor,
            minHeight: 270,
            height: '100%',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            background: `radial-gradient(circle at 94% 10%, ${alpha(accentColor,.12)}, transparent 30%), linear-gradient(145deg, rgba(255,255,255,.995), ${alpha(accentColor,.06)}) !important`,
            border: `1px solid ${alpha(accentColor, selected ? .28 : .13)} !important`,
            boxShadow: selected
                ? `0 18px 38px ${alpha(accentColor,.18)}, 0 5px 12px rgba(25,57,78,.06) !important`
                : `0 12px 30px rgba(25,57,78,.085), 0 3px 9px ${alpha(accentColor,.04)} !important`,
            transition: 'transform .22s cubic-bezier(0.22,1,0.36,1), box-shadow .22s ease, border-color .22s ease',
            '&:hover': {
                transform: 'translateY(-3px)',
                borderColor: `${alpha(accentColor,.24)} !important`,
                boxShadow: `0 20px 42px ${alpha(accentColor,.15)}, 0 6px 14px rgba(25,57,78,.06) !important`,
            },
        }}
    >
        <Box className="tf-card-dotgrid" sx={{ right:11, top:11, opacity:.08 }} />
        <Box sx={{ position:'absolute', width:145, height:145, right:-52, top:-58, borderRadius:'50%', bgcolor:alpha(accentColor,.08), pointerEvents:'none' }} />
        <Box sx={{ position:'absolute', width:110, height:110, right:28, bottom:-72, borderRadius:'36% 64% 62% 38%', bgcolor:alpha(accentColor,.055), transform:'rotate(26deg)', pointerEvents:'none' }} />
        <Box sx={{ position:'absolute', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${accentColor}, ${alpha(accentColor,.45)}, transparent 88%)`, zIndex:3 }} />
        <Box className="tf-fixed-cover tf-schedule-cover" sx={{
            height:92,
            position:'relative',
            zIndex:1,
            background:coverImage
                ? `linear-gradient(180deg, ${alpha(accentColor,.04)}, ${alpha(accentColor,.12)}), url("${coverImage}") center/cover no-repeat`
                : `linear-gradient(118deg, ${alpha(accentColor,.92)}, ${alpha(palette.teal,.72)})`,
            borderBottom:`1px solid ${alpha(accentColor,.12)}`,
            overflow:'hidden',
        }}>
            {!coverImage && fallbackIcon}
            <Box sx={{ position:'absolute', inset:0, background:'linear-gradient(90deg,rgba(255,255,255,.04),rgba(255,255,255,.22),rgba(255,255,255,.03))', opacity:.55 }} />
        </Box>
        {children}
    </Card>
);

const GlassGridCompanyCard = ({ company, isSelected, onSelect, connectionStatus, index }) => {
    const accentColor = ACCENTS[(company.id?.length || index || 0) % ACCENTS.length];
    const logoImage = getCompanyLogoUrl(company);
    const coverImage = getCompanyCoverUrl(company);
    const initials = (company.companyName || 'C').split(/\s+/).slice(0,2).map((p) => p?.[0] || '').join('').toUpperCase();
    const statusColor = ['calendar_shared','chat_enabled','engaged','connected'].includes(connectionStatus) ? palette.pine : connectionStatus === 'pending' ? palette.gold : connectionStatus === 'declined' ? palette.danger : palette.inkLight;
    const statusLabel = ({ calendar_shared:'Calendar Shared', chat_enabled:'Chat Enabled', engaged:'Introduction Accepted', connected:'Connected', pending:'Pending', declined:'Declined' })[connectionStatus] || 'Discover';
    const lookingForValue = company.lookingFor || company.primaryConnectWith || company.interests || 'Business opportunities';
    const lookingFor = Array.isArray(lookingForValue) ? lookingForValue.slice(0,2).join(', ') : String(lookingForValue || 'Business opportunities');
    const service = company.primaryService || company.companyType || 'Business Network Member';

    return (
        <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ duration:.28, delay:(index||0)*.025 }} style={{ height:'100%' }}>
            <ScheduleSelectionShell
                accentColor={accentColor}
                coverImage={coverImage}
                selected={isSelected}
                onClick={() => onSelect(company)}
                fallbackIcon={<BusinessIcon sx={{ position:'absolute', right:10, bottom:-15, fontSize:72, color:'rgba(255,255,255,.13)', transform:'rotate(-10deg)' }} />}
            >
                <CardContent sx={{ p:'0 13px 13px !important', position:'relative', zIndex:2, display:'flex', flexDirection:'column', flex:1 }}>
                    <Box sx={{ display:'flex', alignItems:'flex-start', gap:1, minWidth:0, mt:-3.2 }}>
                        <Avatar src={logoImage || undefined} className="company-card-icon" sx={{ width:58, height:58, borderRadius:'15px', flexShrink:0, color:logoImage ? palette.ink : getContrastText(accentColor), fontSize:'1rem', fontWeight:850, background:logoImage ? '#fff' : `linear-gradient(145deg,${accentColor},${alpha(accentColor,.68)})`, border:'3px solid #fff', boxShadow:`0 8px 18px ${alpha(accentColor,.24)}`, '& img':{ objectFit:'contain', padding:'4px' } }}>{!logoImage && initials}</Avatar>
                        <Box sx={{ minWidth:0, flex:1 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:.4, minWidth:0 }}><Typography sx={{ fontSize:'.78rem', fontWeight:850, color:palette.ink, lineHeight:1.2 }} noWrap>{company.companyName || 'Company'}</Typography>{company.isVerified && <VerifiedIcon sx={{ color:palette.success, fontSize:14, flexShrink:0 }} />}</Box>
                            <Typography sx={{ mt:.25, fontSize:'.52rem', color:palette.inkFaint }} noWrap>{service}</Typography>
                            <Box sx={{ display:'flex', gap:.45, mt:.55, flexWrap:'wrap' }}>
                                <Box sx={{ px:.7, height:19, borderRadius:'999px', display:'inline-flex', alignItems:'center', color:getReadableColor(accentColor), bgcolor:alpha(accentColor,.075), border:`1px solid ${alpha(accentColor,.10)}`, fontSize:'.48rem', fontWeight:750 }}>{company.companyType || 'Company'}</Box>
                                <Box sx={{ px:.7, height:19, borderRadius:'999px', display:'inline-flex', alignItems:'center', gap:.35, color:statusColor, bgcolor:alpha(statusColor,.065), border:`1px solid ${alpha(statusColor,.10)}`, fontSize:'.48rem', fontWeight:750 }}><Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:statusColor }} />{statusLabel}</Box>
                            </Box>
                        </Box>
                        {isSelected && <Box sx={{ width:24, height:24, borderRadius:'8px', display:'grid', placeItems:'center', color:getGradientContrastText(accentColor,palette.teal), background:`linear-gradient(145deg,${accentColor},${palette.teal})`, boxShadow:`0 5px 12px ${alpha(accentColor,.22)}` }}><CheckIcon sx={{ fontSize:14 }} /></Box>}
                    </Box>

                    <Box sx={{ mt:1, p:.8, borderRadius:'10px', bgcolor:alpha(accentColor,.04), border:`1px solid ${alpha(accentColor,.075)}` }}>
                        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:.8 }}><Typography sx={{ fontSize:'.5rem', fontWeight:760, color:palette.inkLight, textTransform:'uppercase', letterSpacing:'.055em' }}>Meeting Access</Typography><Typography sx={{ fontSize:'.58rem', fontWeight:850, color:statusColor }}>{statusLabel}</Typography></Box>
                        <Box sx={{ mt:.45, height:4, borderRadius:99, bgcolor:alpha(statusColor,.10), overflow:'hidden' }}><Box sx={{ height:'100%', width:`${scheduleAccessProgress(connectionStatus)}%`, borderRadius:99, background:`linear-gradient(90deg,${alpha(statusColor,.72)},${statusColor})` }} /></Box>
                    </Box>

                    <Box sx={{ mt:.8, display:'grid', gridTemplateColumns:'1fr 1fr', gap:.55 }}>
                        <Box sx={{ minWidth:0, display:'flex', alignItems:'center', gap:.45, color:palette.inkLight }}><PublicIcon sx={{ fontSize:12, color:getReadableColor(accentColor), flexShrink:0 }} /><Typography sx={{ fontSize:'.5rem' }} noWrap>{company.primaryRegion || company.country || 'Global'}</Typography></Box>
                        <Box sx={{ minWidth:0, display:'flex', alignItems:'center', gap:.45, color:palette.inkLight }}><HandshakeIcon sx={{ fontSize:12, color:getReadableColor(accentColor), flexShrink:0 }} /><Typography sx={{ fontSize:'.5rem' }} noWrap>{lookingFor}</Typography></Box>
                    </Box>

                    <Box sx={{ mt:'auto', pt:.9 }}><Button fullWidth size="small" startIcon={isSelected ? <CheckIcon sx={{ fontSize:13 }} /> : <BusinessIcon sx={{ fontSize:13 }} />} sx={{ minHeight:28, color:`${getGradientContrastText(accentColor,palette.purple)} !important`, background:`linear-gradient(135deg,${accentColor},${palette.purple}) !important`, fontSize:'.55rem !important', boxShadow:`0 7px 16px ${alpha(accentColor,.20)} !important` }}>{isSelected ? 'Selected Company' : 'Select Company'}</Button></Box>
                </CardContent>
            </ScheduleSelectionShell>
        </motion.div>
    );
};

const GlassUserCard = ({ user, company, isSelected, onSelect, connectionStatus, index }) => {
    const accentColor = ACCENTS[(user.id?.length || index || 0) % ACCENTS.length];
    const name = user.displayName || [user.firstName,user.lastName].filter(Boolean).join(' ') || user.name || 'User';
    const initials = name.split(/\s+/).slice(0,2).map((p) => p?.[0] || '').join('').toUpperCase();
    const statusColor = connectionStatus === 'calendar_shared' ? palette.pine : ['chat_enabled','engaged','connected'].includes(connectionStatus) ? palette.teal : connectionStatus === 'pending' ? palette.gold : connectionStatus === 'declined' ? palette.danger : palette.inkLight;
    const statusLabel = ({ calendar_shared:'Calendar Shared', chat_enabled:'Chat Enabled', engaged:'Introduction Accepted', connected:'Connected', pending:'Pending', declined:'Declined' })[connectionStatus] || 'Intro Required';
    const serviceValue = user.primaryServiceProvided || user.primaryService || user.specialization || 'Business professional';
    const service = Array.isArray(serviceValue) ? serviceValue.slice(0,2).join(', ') : String(serviceValue || 'Business professional');
    const seekingValue = user.servicesSought || user.lookingFor || user.productsSought || user.buyingInterests || 'Opportunities';
    const seeking = Array.isArray(seekingValue) ? seekingValue.slice(0,2).join(', ') : String(seekingValue || 'Opportunities');
    const personPhoto = getPersonPhotoUrl(user);
    const companyCover = getCompanyCoverUrl(company || {}) || (!company?.id ? (user.companyCoverImage || user.companyCoverUrl || '') : '');
    const companyLogo = getCompanyLogoUrl(company || {}) || (!company?.id ? (user.companyLogo || user.companyLogoUrl || '') : '');

    return (
        <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ duration:.28, delay:(index||0)*.025 }} style={{ height:'100%' }}>
            <ScheduleSelectionShell
                accentColor={accentColor}
                coverImage={companyCover}
                selected={isSelected}
                onClick={() => onSelect(user)}
                fallbackIcon={<PeopleIcon sx={{ position:'absolute', right:10, bottom:-16, fontSize:72, color:'rgba(255,255,255,.13)', transform:'rotate(-10deg)' }} />}
            >
                <CardContent sx={{ p:'0 13px 13px !important', position:'relative', zIndex:2, display:'flex', flexDirection:'column', flex:1 }}>
                    <Box sx={{ display:'flex', alignItems:'flex-start', gap:1, minWidth:0, mt:-3.2 }}>
                        <Box className="tf-person-company-identity" sx={{ position:'relative', flexShrink:0, width:62, height:66, display:'flex', justifyContent:'center' }}>
                            <Avatar src={personPhoto || undefined} className="person-card-avatar" sx={{ width:58, height:58, borderRadius:'50%', color:getContrastText(accentColor), fontWeight:850, fontSize:'1rem', background:`linear-gradient(145deg,${accentColor},${alpha(accentColor,.68)})`, border:'3px solid #fff', boxShadow:`0 8px 18px ${alpha(accentColor,.24)}` }}>{initials}</Avatar>
                            {companyLogo && <Avatar src={companyLogo} sx={{ position:'absolute', left:'50%', transform:'translateX(-50%)', bottom:0, width:23, height:23, borderRadius:'50%', bgcolor:'#fff', border:'2px solid #fff', boxShadow:'0 4px 9px rgba(25,57,78,.16)', '& img':{ objectFit:'contain', padding:'2px' } }} />}
                        </Box>
                        <Box sx={{ minWidth:0, flex:1 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:.4, minWidth:0 }}><Typography sx={{ fontSize:'.78rem', fontWeight:850, color:palette.ink, lineHeight:1.2 }} noWrap>{name}</Typography>{user.isVerified && <VerifiedIcon sx={{ color:palette.success, fontSize:14, flexShrink:0 }} />}</Box>
                            <Typography sx={{ mt:.25, fontSize:'.52rem', color:palette.inkFaint }} noWrap>{user.designation || user.jobTitle || user.role || 'Team Member'} · {user.companyName || user.company || company?.companyName || 'Company'}</Typography>
                            <Box sx={{ display:'flex', gap:.45, mt:.55, flexWrap:'wrap' }}>
                                <Box sx={{ px:.7, height:19, borderRadius:'999px', display:'inline-flex', alignItems:'center', color:getReadableColor(accentColor), bgcolor:alpha(accentColor,.075), border:`1px solid ${alpha(accentColor,.10)}`, fontSize:'.48rem', fontWeight:750 }}>{user.country || user.region || 'Global'}</Box>
                                <Box sx={{ px:.7, height:19, borderRadius:'999px', display:'inline-flex', alignItems:'center', gap:.35, color:statusColor, bgcolor:alpha(statusColor,.065), border:`1px solid ${alpha(statusColor,.10)}`, fontSize:'.48rem', fontWeight:750 }}><Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:statusColor }} />{statusLabel}</Box>
                            </Box>
                        </Box>
                        {isSelected && <Box sx={{ width:24, height:24, borderRadius:'8px', display:'grid', placeItems:'center', color:getGradientContrastText(accentColor,palette.teal), background:`linear-gradient(145deg,${accentColor},${palette.teal})`, boxShadow:`0 5px 12px ${alpha(accentColor,.22)}` }}><CheckIcon sx={{ fontSize:14 }} /></Box>}
                    </Box>

                    <Box sx={{ mt:1, p:.8, borderRadius:'10px', bgcolor:alpha(accentColor,.04), border:`1px solid ${alpha(accentColor,.075)}` }}>
                        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:.8 }}><Typography sx={{ fontSize:'.5rem', fontWeight:760, color:palette.inkLight, textTransform:'uppercase', letterSpacing:'.055em' }}>Meeting Access</Typography><Typography sx={{ fontSize:'.58rem', fontWeight:850, color:statusColor }}>{statusLabel}</Typography></Box>
                        <Box sx={{ mt:.45, height:4, borderRadius:99, bgcolor:alpha(statusColor,.10), overflow:'hidden' }}><Box sx={{ height:'100%', width:`${scheduleAccessProgress(connectionStatus)}%`, borderRadius:99, background:`linear-gradient(90deg,${alpha(statusColor,.72)},${statusColor})` }} /></Box>
                    </Box>

                    <Box sx={{ mt:.8, display:'grid', gridTemplateColumns:'1fr 1fr', gap:.55 }}>
                        <Box sx={{ minWidth:0, display:'flex', alignItems:'center', gap:.45, color:palette.inkLight }}><WorkIcon sx={{ fontSize:12, color:getReadableColor(accentColor), flexShrink:0 }} /><Typography sx={{ fontSize:'.5rem' }} noWrap>{service}</Typography></Box>
                        <Box sx={{ minWidth:0, display:'flex', alignItems:'center', gap:.45, color:palette.inkLight }}><ShoppingCartIcon sx={{ fontSize:12, color:getReadableColor(accentColor), flexShrink:0 }} /><Typography sx={{ fontSize:'.5rem' }} noWrap>{seeking}</Typography></Box>
                    </Box>

                    <Box sx={{ mt:'auto', pt:.9 }}><Button fullWidth size="small" startIcon={isSelected ? <CheckIcon sx={{ fontSize:13 }} /> : <PersonIcon sx={{ fontSize:13 }} />} sx={{ minHeight:28, color:`${getGradientContrastText(accentColor,palette.purple)} !important`, background:`linear-gradient(135deg,${accentColor},${palette.purple}) !important`, fontSize:'.55rem !important', boxShadow:`0 7px 16px ${alpha(accentColor,.20)} !important` }}>{isSelected ? 'Selected Person' : 'Select Person'}</Button></Box>
                </CardContent>
            </ScheduleSelectionShell>
        </motion.div>
    );
};


const GlassSkeletonGridCard = () => (
    <Box sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Skeleton variant="circular" width={72} height={72} sx={{ borderRadius: '14px', mb: 1.5 }} />
        <Skeleton variant="text" width="60%" height={28} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="50%" height={16} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: '4px' }} />
            <Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: '4px' }} />
        </Box>
    </Box>
);

// ============================================
// MAIN SCHEDULE MEETING COMPONENT
// ============================================

const ScheduleMeeting = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData, companyData, currentUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [selectedEntityType, setSelectedEntityType] = useState(null);
    const [allCompanies, setAllCompanies] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [connectionStatuses, setConnectionStatuses] = useState({});
    const [userConnectionStatuses, setUserConnectionStatuses] = useState({});
    const [sharedAvailabilityByUser, setSharedAvailabilityByUser] = useState({});
    const [requestingAvailability, setRequestingAvailability] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [formData, setFormData] = useState({
        title: '',
        date: new Date(new Date().setHours(10, 0, 0, 0)),
        duration: 30,
        type: 'video',
        message: '',
        agenda: '',
        isAIIntro: false,
        aiIntroMessage: '',
    });
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [scheduling, setScheduling] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [connectionFilter, setConnectionFilter] = useState('all');
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [showSuccess, setShowSuccess] = useState(false);
    const [selectedCompanyScopeId, setSelectedCompanyScopeId] = useState('');
    const [scheduledMeeting, setScheduledMeeting] = useState(null);
    const [generatingAI, setGeneratingAI] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);

    const getCompanyId = useCallback(() => {
        const id = resolveCompanyId({ companyData, userData, currentUser });
        if (!id) console.warn('No company ID found in auth context');
        return id;
    }, [companyData, userData, currentUser]);

    const companyId = getCompanyId();
    const companyName = companyData?.companyName || userData?.companyName || 'Your Company';

    // ============================================
    // FETCH CONNECTION STATUSES
    // ============================================

    useEffect(() => {
        if (!companyId) return;

        const invitationsRef = collection(db, 'invitations');
        const qSent = query(invitationsRef, where('companyId', '==', companyId));
        const qReceived = query(invitationsRef, where('targetCompanyId', '==', companyId));

        let sentData = [];
        let receivedData = [];

        const updateConnectionMap = () => {
            const newMap = {};
            sentData.forEach(docSnap => {
                const data = docSnap.data();
                if (data.targetCompanyId) {
                    const status = data.status === 'accepted' || data.status === 'connected' ? 'connected' : (data.status || 'pending');
                    if (status === 'connected' || !newMap[data.targetCompanyId]) {
                        newMap[data.targetCompanyId] = status;
                    }
                }
            });
            receivedData.forEach(docSnap => {
                const data = docSnap.data();
                if (data.companyId) {
                    const status = data.status === 'accepted' || data.status === 'connected' ? 'connected' : (data.status || 'pending');
                    if (status === 'connected' || !newMap[data.companyId]) {
                        newMap[data.companyId] = status;
                    }
                }
            });
            setConnectionStatuses(newMap);
        };

        const unsubSent = onSnapshot(qSent, (snap) => {
            sentData = snap.docs;
            updateConnectionMap();
        });

        const unsubReceived = onSnapshot(qReceived, (snap) => {
            receivedData = snap.docs;
            updateConnectionMap();
        });

        let unsubAiSent = () => {};
        let unsubAiReceived = () => {};
        let unsubIntroSent = () => {};
        let unsubIntroReceived = () => {};

        if (currentUser?.uid) {
            const aiRef = collection(db, 'ai_introductions');
            const introRef = collection(db, 'introductions');
            const aiSentQ = query(aiRef, where('senderUserId', '==', currentUser.uid));
            const aiReceivedQ = query(aiRef, where('targetUserId', '==', currentUser.uid));
            const introSentQ = query(introRef, where('fromUserId', '==', currentUser.uid));
            const introReceivedQ = query(introRef, where('toUserId', '==', currentUser.uid));

            let aiSentData = [];
            let aiReceivedData = [];
            let introSentData = [];
            let introReceivedData = [];

            const updateUserConnectionMap = () => {
                const newMap = {};
                const availabilityMap = {};
                const rank = { blocked: 0, not_connected: 1, pending: 2, engaged: 3, chat_enabled: 4, calendar_shared: 5 };
                const applyStatus = (userId, data = {}, direction = 'sent') => {
                    if (!userId) return;
                    const rawStatus = String(data.status || 'pending').toLowerCase();
                    let status = rawStatus;
                    if (direction === 'sent') {
                        if (data.calendarShared || data.recipientDecision === 'calendar' || data.recipientDecision === 'both') status = 'calendar_shared';
                        else if (data.chatEnabled || data.recipientDecision === 'chat') status = 'chat_enabled';
                        else if (rawStatus === 'accepted' || rawStatus === 'connected') status = 'engaged';
                    } else if (rawStatus === 'accepted' || rawStatus === 'connected') {
                        // The recipient granting access to their own calendar does not
                        // grant reverse access to the sender's calendar.
                        status = 'engaged';
                    }
                    const previous = newMap[userId];
                    if (!previous || (rank[status] || 1) > (rank[previous] || 1)) newMap[userId] = status;
                    if (direction === 'sent' && Array.isArray(data.sharedAvailability) && data.sharedAvailability.length) {
                        availabilityMap[userId] = data.sharedAvailability;
                    }
                };

                aiSentData.forEach(docSnap => {
                    const data = docSnap.data();
                    applyStatus(data.targetUserId, data, 'sent');
                });
                aiReceivedData.forEach(docSnap => {
                    const data = docSnap.data();
                    applyStatus(data.senderUserId, data, 'received');
                });
                introSentData.forEach(docSnap => {
                    const data = docSnap.data();
                    applyStatus(data.toUserId, data, 'sent');
                });
                introReceivedData.forEach(docSnap => {
                    const data = docSnap.data();
                    applyStatus(data.fromUserId, data, 'received');
                });
                setUserConnectionStatuses(newMap);
                setSharedAvailabilityByUser(availabilityMap);
            };

            unsubAiSent = onSnapshot(aiSentQ, (snap) => {
                aiSentData = snap.docs;
                updateUserConnectionMap();
            });
            unsubAiReceived = onSnapshot(aiReceivedQ, (snap) => {
                aiReceivedData = snap.docs;
                updateUserConnectionMap();
            });
            unsubIntroSent = onSnapshot(introSentQ, (snap) => {
                introSentData = snap.docs;
                updateUserConnectionMap();
            });
            unsubIntroReceived = onSnapshot(introReceivedQ, (snap) => {
                introReceivedData = snap.docs;
                updateUserConnectionMap();
            });
        } else {
            setUserConnectionStatuses({});
            setSharedAvailabilityByUser({});
        }

        return () => {
            unsubSent();
            unsubReceived();
            unsubAiSent();
            unsubAiReceived();
            unsubIntroSent();
            unsubIntroReceived();
        };
    }, [companyId, currentUser]);

    // ============================================
    // FETCH COMPANIES & USERS (Only Connected)
    // ============================================

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        try {
            // Always load the directory itself. Invitation state is metadata,
            // not a prerequisite for visibility. This keeps legacy companies
            // and not-yet-connected companies visible instead of rendering an
            // empty Schedule Meeting page.
            const snapshot = await getDocs(collection(db, 'companies'));
            const companiesList = snapshot.docs
                .filter((snap) => !['_schema', '_placeholder'].includes(snap.id))
                .map((snap) => {
                    const data = snap.data() || {};
                    const entityCompanyId = data.companyId || snap.id;
                    return {
                        id: snap.id,
                        companyId: entityCompanyId,
                        ...data,
                        connectionStatus: connectionStatuses[snap.id] || connectionStatuses[entityCompanyId] || 'not_connected',
                    };
                })
                .filter((company) => {
                    const id = company.companyId || company.id;
                    return id !== companyId && company.id !== companyId && company.isBlocked !== true;
                })
                .sort((a, b) => (a.companyName || a.name || '').localeCompare(b.companyName || b.name || '', undefined, { sensitivity: 'base' }));

            setAllCompanies(companiesList);
            setHasMore(false);
            setLastDoc(null);
        } catch (error) {
            console.error('Error fetching companies for scheduling:', error);
            setAllCompanies([]);
            toast.error(friendlyError(error, 'Failed to load companies'));
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [companyId, connectionStatuses]);

    const fetchUsers = useCallback(async () => {
        if (!currentUser?.uid) {
            setAllUsers([]);
            return;
        }

        try {
            const snapshot = await getDocs(collection(db, 'users'));
            const usersList = snapshot.docs
                .filter((snap) => !['_schema', '_placeholder'].includes(snap.id))
                .map((docSnap) => {
                    const data = docSnap.data() || {};
                    const userId = data.uid || data.userId || docSnap.id;
                    const status = userConnectionStatuses[userId] || userConnectionStatuses[docSnap.id] || 'not_connected';
                    return {
                        id: docSnap.id,
                        uid: userId,
                        displayName: data.displayName || [data.firstName, data.lastName].filter(Boolean).join(' ') || data.name || 'Unknown User',
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        email: data.email || '',
                        role: data.role || 'Team Member',
                        designation: data.designation || data.role || data.title || '',
                        businessTitle: data.businessTitle || data.title || '',
                        department: data.department || '',
                        companyName: data.companyName || data.company || '',
                        companyId: data.companyId || data.company?.id || data.company?.companyId || '',
                        photoURL: getPersonPhotoUrl(data),
                        isOnline: data.isOnline || false,
                        isVerified: data.isVerified || false,
                        country: data.country || data.region || '',
                        region: data.region || data.country || '',
                        location: data.location || data.country || '',
                        specialization: data.specialization || data.expertise || '',
                        expertise: data.expertise || data.specialization || '',
                        primaryService: data.primaryService || data.primaryServiceProvided || '',
                        primaryServiceProvided: data.primaryServiceProvided || data.primaryService || '',
                        secondaryService: data.secondaryService || data.secondaryServiceProvided || '',
                        secondaryServiceProvided: data.secondaryServiceProvided || data.secondaryService || '',
                        primaryRegion: data.primaryRegion || data.primaryRegionAvailability || '',
                        primaryRegionAvailability: data.primaryRegionAvailability || data.primaryRegion || '',
                        secondaryRegion: data.secondaryRegion || data.secondaryRegionAvailability || '',
                        secondaryRegionAvailability: data.secondaryRegionAvailability || data.secondaryRegion || '',
                        primaryConnectWith: data.primaryConnectWith || '',
                        secondaryConnectWith: data.secondaryConnectWith || '',
                        otherConnectWith: data.otherConnectWith || '',
                        primaryServiceBuying: data.primaryServiceBuying || '',
                        secondaryServiceBuying: data.secondaryServiceBuying || '',
                        preferredRegion: data.preferredRegion || '',
                        secondaryPreferredRegion: data.secondaryPreferredRegion || '',
                        interests: data.interests || data.interestToBuy || [],
                        interestToBuy: data.interestToBuy || data.interests || [],
                        skills: data.skills || [],
                        description: data.description || data.bio || '',
                        bio: data.bio || data.description || '',
                        phone: data.phone || data.phoneNumber || '',
                        dialCode: data.dialCode || '',
                        linkedinProfile: data.linkedinProfile || '',
                        connectionStatus: status,
                        ...data,
                    };
                })
                .filter((user) => {
                    const userId = user.uid || user.id;
                    if (userId === currentUser.uid || user.id === currentUser.uid) return false;
                    if (user.isBlocked === true || user.isActive === false || user.isApproved === false) return false;
                    // TELCOFAIR meetings are external business meetings; don't
                    // surface the logged-in company's own team as targets.
                    if (companyId && user.companyId === companyId) return false;
                    return true;
                })
                .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', undefined, { sensitivity: 'base' }));

            setAllUsers(usersList);
        } catch (error) {
            console.error('Error fetching users for scheduling:', error);
            setAllUsers([]);
            toast.error(friendlyError(error, 'Failed to load people'));
        }
    }, [companyId, currentUser?.uid, userConnectionStatuses]);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        const routeCompany = location.state?.company;
        const routeUser = location.state?.user || location.state?.inviteUser;

        if (routeCompany) {
            const routeCompanyId = routeCompany.companyId || routeCompany.id || '';
            setSelectedCompanyScopeId(routeCompanyId);
            setSelectedEntity(null);
            setSelectedEntityType(null);
            setTabValue(1);
        } else if (routeUser) {
            setSelectedEntity(routeUser);
            setSelectedEntityType('user');
            setTabValue(1);
        }
    }, [location.state]);

    // ============================================
    // FILTER ENTITIES (Only show connected)
    // ============================================

    const filteredCompanies = useMemo(() => {
        let filtered = [...allCompanies];

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(company =>
                company.companyName?.toLowerCase().includes(searchLower) ||
                company.country?.toLowerCase().includes(searchLower) ||
                company.companyType?.toLowerCase().includes(searchLower) ||
                company.primaryService?.toLowerCase().includes(searchLower) ||
                company.primaryRegion?.toLowerCase().includes(searchLower) ||
                company.specialization?.toLowerCase().includes(searchLower) ||
                (company.interests && company.interests.some(i => i.toLowerCase().includes(searchLower)))
            );
        }

        if (connectionFilter !== 'all') {
            filtered = filtered.filter(company => {
                const status = connectionStatuses[company.id] || 'not_connected';
                if (connectionFilter === 'connected') return status === 'connected';
                if (connectionFilter === 'pending') return status === 'pending';
                if (connectionFilter === 'not_connected') return status === 'not_connected' || status === 'declined';
                return true;
            });
        }

        return filtered;
    }, [allCompanies, searchTerm, connectionFilter, connectionStatuses]);

    const filteredUsers = useMemo(() => {
        let filtered = [...allUsers];

        if (selectedCompanyScopeId) {
            filtered = filtered.filter((user) => String(user.companyId || '') === String(selectedCompanyScopeId));
        }

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(user =>
                user.displayName?.toLowerCase().includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower) ||
                user.designation?.toLowerCase().includes(searchLower) ||
                user.department?.toLowerCase().includes(searchLower) ||
                user.companyName?.toLowerCase().includes(searchLower) ||
                user.specialization?.toLowerCase().includes(searchLower) ||
                user.expertise?.toLowerCase().includes(searchLower) ||
                user.primaryService?.toLowerCase().includes(searchLower) ||
                user.primaryServiceProvided?.toLowerCase().includes(searchLower) ||
                user.region?.toLowerCase().includes(searchLower) ||
                user.country?.toLowerCase().includes(searchLower) ||
                user.primaryConnectWith?.toLowerCase().includes(searchLower) ||
                user.secondaryConnectWith?.toLowerCase().includes(searchLower) ||
                user.primaryServiceBuying?.toLowerCase().includes(searchLower) ||
                (user.interests && user.interests.some(i => i.toLowerCase().includes(searchLower))) ||
                (user.skills && user.skills.some(s => s.toLowerCase().includes(searchLower)))
            );
        }

        if (connectionFilter !== 'all') {
            filtered = filtered.filter(user => {
                const status = userConnectionStatuses[user.uid || user.id] || 'not_connected';
                if (connectionFilter === 'connected') return ['calendar_shared', 'chat_enabled', 'engaged', 'connected'].includes(status);
                if (connectionFilter === 'pending') return status === 'pending';
                if (connectionFilter === 'not_connected') return status === 'not_connected' || status === 'declined';
                return true;
            });
        }

        return filtered;
    }, [allUsers, searchTerm, connectionFilter, userConnectionStatuses, selectedCompanyScopeId]);

    // ============================================
    // GENERATE AVAILABLE SLOTS
    // ============================================

    const generateAvailableSlots = useCallback(async () => {
        const targetUserId = selectedEntityType === 'user' ? (selectedEntity?.uid || selectedEntity?.userId || selectedEntity?.id) : null;
        if (!targetUserId) return [];

        const selectedDate = new Date(`${formData.date}T00:00:00`);
        if (Number.isNaN(selectedDate.getTime())) return [];

        // Availability belongs to the recipient. Prefer user-specific hours and
        // then their company working hours; never use the sender's hours.
        let workingHours = selectedEntity?.workingHours || {};
        const targetCompanyId = selectedEntity?.companyId || selectedEntity?.company?.id || '';
        if ((!workingHours.start && !workingHours.end) && targetCompanyId) {
            try {
                const companySnap = await getDoc(doc(db, 'companies', targetCompanyId));
                if (companySnap.exists()) workingHours = companySnap.data()?.workingHours || workingHours;
            } catch (hoursError) {
                console.warn('Unable to load recipient working hours:', hoursError);
            }
        }

        const [startHourRaw, startMinuteRaw = 0] = String(workingHours.start || '08:00').split(':').map(Number);
        const [endHourRaw, endMinuteRaw = 0] = String(workingHours.end || '18:00').split(':').map(Number);
        const startMinutes = (Number.isFinite(startHourRaw) ? startHourRaw : 8) * 60 + (Number.isFinite(startMinuteRaw) ? startMinuteRaw : 0);
        const endMinutes = (Number.isFinite(endHourRaw) ? endHourRaw : 18) * 60 + (Number.isFinite(endMinuteRaw) ? endMinuteRaw : 0);
        const workingDays = Array.isArray(workingHours.days) ? workingHours.days.map(v => String(v).toLowerCase()) : [];
        const dayName = selectedDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const dayAllowed = !workingDays.length || workingDays.some(day => day === dayName || day.startsWith(dayName.slice(0, 3)) || dayName.startsWith(day.slice(0, 3)));
        const interval = Math.max(15, Number(workingHours.slotMinutes || 30));
        const duration = Number(formData.duration) || Number(workingHours.defaultMeetingDuration) || Number(selectedEntity?.defaultMeetingDuration) || 30;
        const now = new Date();
        const sharedAvailability = Array.isArray(sharedAvailabilityByUser[targetUserId]) ? sharedAvailabilityByUser[targetUserId] : [];
        const dayStart = new Date(selectedDate); dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(selectedDate); dayEnd.setHours(23, 59, 59, 999);
        const sharedDayWindows = sharedAvailability.map((window) => ({ start: new Date(window.start), end: new Date(window.end) }))
            .filter((window) => !Number.isNaN(window.start.getTime()) && !Number.isNaN(window.end.getTime()) && window.start <= dayEnd && window.end >= dayStart);
        const sharedStartMinutes = sharedDayWindows.length ? Math.min(...sharedDayWindows.map((window) => window.start.getHours() * 60 + window.start.getMinutes())) : startMinutes;
        const sharedEndMinutes = sharedDayWindows.length ? Math.max(...sharedDayWindows.map((window) => window.end.getHours() * 60 + window.end.getMinutes())) : endMinutes;
        const loopStartMinutes = sharedAvailability.length ? sharedStartMinutes : startMinutes;
        const loopEndMinutes = sharedAvailability.length ? sharedEndMinutes : endMinutes;

        const byId = new Map();
        try {
            const meetingsRef = collection(db, 'meetings');
            const snapshots = await Promise.all([
                getDocs(query(meetingsRef, where('participantIds', 'array-contains', targetUserId))),
                getDocs(query(meetingsRef, where('requesterUserId', '==', targetUserId))),
                getDocs(query(meetingsRef, where('targetUserId', '==', targetUserId))),
            ]);
            snapshots.forEach(snapshot => snapshot.docs.forEach(snap => byId.set(snap.id, { id: snap.id, ...snap.data() })));
        } catch (error) {
            console.warn('Could not load recipient TELCOFAIR meetings:', error);
        }

        const busyRanges = [...byId.values()]
            .filter(meeting => !['cancelled', 'declined', 'completed'].includes(String(meeting.status || '').toLowerCase()))
            .map(meeting => {
                const raw = meeting.scheduledTime;
                const slotStart = raw?.toDate ? raw.toDate() : raw ? new Date(raw) : meeting.date ? new Date(`${meeting.date}T${meeting.time || '00:00'}`) : null;
                if (!slotStart || Number.isNaN(slotStart.getTime())) return null;
                const slotEnd = new Date(slotStart.getTime() + (Number(meeting.duration) || 30) * 60000);
                return { start: slotStart, end: slotEnd, source: 'telcofair' };
            })
            .filter(Boolean);

        // External Google / Outlook busy time is returned without event titles,
        // preserving privacy while preventing double booking.
        try {
            const external = await getBusyTimes({
                companyId,
                targetUserId,
                start: dayStart.toISOString(),
                end: dayEnd.toISOString(),
            });
            (external?.busy || []).forEach((item) => {
                const busyStart = new Date(item.start);
                const busyEnd = new Date(item.end);
                if (!Number.isNaN(busyStart.getTime()) && !Number.isNaN(busyEnd.getTime())) busyRanges.push({ start: busyStart, end: busyEnd, source: item.provider || 'external' });
            });
        } catch (externalError) {
            console.warn('External calendar availability preview unavailable:', externalError);
            // The backend bookMeeting function performs the authoritative check.
        }

        const slots = [];
        for (let minuteOfDay = loopStartMinutes; minuteOfDay < loopEndMinutes; minuteOfDay += interval) {
            const hour = Math.floor(minuteOfDay / 60);
            const min = minuteOfDay % 60;
            const slotStart = new Date(selectedDate);
            slotStart.setHours(hour, min, 0, 0);
            const slotEnd = new Date(slotStart.getTime() + duration * 60000);
            const slotEndMinute = slotEnd.getHours() * 60 + slotEnd.getMinutes();
            const withinSharedWindow = !sharedAvailability.length || sharedDayWindows.some((window) => slotStart >= window.start && slotEnd <= window.end);
            const withinWorkingHours = sharedAvailability.length ? withinSharedWindow : (dayAllowed && slotEndMinute <= endMinutes);
            const notPast = slotStart > now;
            const hasConflict = busyRanges.some(range => slotStart < range.end && slotEnd > range.start);
            const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
            slots.push({
                time,
                display: `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`,
                available: withinWorkingHours && notPast && !hasConflict,
                selected: false,
            });
        }
        return slots;
    }, [selectedEntity, selectedEntityType, companyId, formData.date, formData.duration, sharedAvailabilityByUser]);

    // ============================================
    // GENERATE AI INTRO
    // ============================================

    const generateAIIntro = useCallback(async (entity = selectedEntity, entityType = selectedEntityType) => {
        if (!companyId || !entity) return;
        setGeneratingAI(true);
        try {
            const targetUserId = entityType === 'user' ? (entity.uid || entity.userId || entity.id) : undefined;
            const targetCompanyId = entityType === 'company' ? (entity.companyId || entity.id) : (entity.companyId || undefined);
            const result = await generateMeetingRequest({
                companyId,
                targetUserId,
                targetCompanyId,
                context: {
                    agenda: formData.agenda || 'Business discussion',
                    meetingType: formData.type || 'video',
                    duration: Number(formData.duration) || 30,
                    source: 'schedule_meeting',
                },
                tone: 'professional',
            });
            const text = result?.text || '';
            if (!text) throw new Error('AI returned an empty draft.');
            setFormData(prev => ({ ...prev, aiIntroMessage: text, message: text, isAIIntro: true }));
            toast.success('ChatGPT meeting request generated.');
        } catch (error) {
            console.error('Error generating ChatGPT request:', error);
            const fallbackName = entity?.displayName || entity?.companyName || 'there';
            const fallback = `Hello ${fallbackName}, I would like to discuss a potential business collaboration with ${companyName}. If the proposed time is convenient, I look forward to speaking with you.`;
            setFormData(prev => ({ ...prev, aiIntroMessage: fallback, message: fallback, isAIIntro: false }));
            toast.error(friendlyError(error, 'ChatGPT AI is unavailable. A standard editable draft was created instead.'));
        } finally {
            setGeneratingAI(false);
        }
    }, [selectedEntity, selectedEntityType, companyId, companyName, formData.agenda, formData.type, formData.duration]);

    // ============================================
    // STEP HANDLERS
    // ============================================

    const handleNext = async () => {
        if (activeStep === 0) {
            if (!selectedEntity) {
                toast.error('Please select a user who shared their calendar');
                return;
            }
            if (selectedEntityType !== 'user') {
                toast.error('Meetings are booked with a person after that recipient shares their calendar. Open the company and choose an eligible user.');
                return;
            }
            const userKey = selectedEntity.uid || selectedEntity.userId || selectedEntity.id;
            if (userConnectionStatuses[userKey] !== 'calendar_shared') {
                setRequestingAvailability(true);
                try {
                    const result = await introductionService.requestAvailability(companyId, {
                        targetUserId: userKey,
                        message: formData.message.trim() || formData.aiIntroMessage || `I would like to schedule a ${formData.duration || 30}-minute business meeting with you. Please share the dates and times that work for you.`,
                        agenda: formData.agenda.trim() || 'Business discussion',
                        duration: Number(formData.duration) || 30,
                    });
                    setUserConnectionStatuses((prev) => ({ ...prev, [userKey]: prev[userKey] === 'engaged' || prev[userKey] === 'chat_enabled' ? prev[userKey] : 'pending' }));
                    toast.success(result?.alreadyRequested ? 'An availability request is already pending with this user.' : 'Meeting availability request sent by email and notification. The recipient will choose the time windows to share.');
                } catch (error) {
                    console.error('Could not request meeting availability:', error);
                    toast.error(friendlyError(error, 'Could not send the meeting availability request.'));
                } finally {
                    setRequestingAvailability(false);
                }
                return;
            }
            setLoadingSlots(true);
            try {
                const slots = await generateAvailableSlots();
                setAvailableSlots(slots);
                setSelectedSlot(null);
            } finally {
                setLoadingSlots(false);
            }
        }
        if (activeStep === 1 && !selectedSlot) {
            toast.error('Please select a time slot');
            return;
        }
        setActiveStep(prev => Math.min(prev + 1, 2));
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const handleEntitySelect = (entity, type) => {
        if (type === 'company') {
            const targetCompanyId = entity?.companyId || entity?.id || '';
            setSelectedCompanyScopeId(targetCompanyId);
            setSelectedEntity(null);
            setSelectedEntityType(null);
            setSearchTerm('');
            setConnectionFilter('all');
            setTabValue(1);
            toast(`Choose a person at ${entity?.companyName || 'this company'} to continue.`, { icon: '👤' });
            return;
        }
        setSelectedEntity(entity);
        setSelectedEntityType(type);
        generateAIIntro(entity, type);
    };

    const handleSlotSelect = (slotIndex) => {
        const updated = availableSlots.map((slot, i) => ({ ...slot, selected: i === slotIndex }));
        setAvailableSlots(updated);
        setSelectedSlot(updated[slotIndex] || null);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setSelectedEntity(null);
        setSelectedEntityType(null);
        setSearchTerm('');
        if (newValue === 0) setSelectedCompanyScopeId('');
    };

    // ============================================
    // SCHEDULE MEETING / SEND AI INTRO
    // ============================================

    const handleSchedule = async () => {
        if (!selectedEntity) { toast.error('Please select a user who shared their calendar'); return; }
        if (selectedEntityType !== 'user') { toast.error('Choose a specific user whose calendar has been shared.'); return; }
        if (!selectedSlot) { toast.error('Please select a time slot'); return; }

        const entityUserId = selectedEntity.uid || selectedEntity.userId || selectedEntity.id;
        if (userConnectionStatuses[entityUserId] !== 'calendar_shared') {
            toast.error('Calendar access is not available for this user.');
            return;
        }

        setScheduling(true);
        try {
            const date = new Date(formData.date);
            const [hours, minutes] = selectedSlot.time.split(':').map(Number);
            date.setHours(hours, minutes, 0, 0);
            const entityName = selectedEntity.displayName || selectedEntity.name || selectedEntity.companyName || 'TELCOFAIR contact';

            const result = await meetingWorkflowService.book(companyId, {
                targetUserId: entityUserId,
                scheduledTime: date.toISOString(),
                duration: Number(formData.duration) || 30,
                type: formData.type || 'video',
                title: formData.title.trim() || `Meeting with ${entityName}`,
                message: formData.message.trim() || formData.aiIntroMessage || '',
                agenda: formData.agenda.trim() || 'Business discussion',
            });

            const meetingData = {
                ...(result?.meeting || {}),
                id: result?.meetingId,
                scheduledTime: date,
                targetName: entityName,
                date: date.toISOString().slice(0, 10),
                time: selectedSlot.time,
            };
            setScheduledMeeting(meetingData);
            setShowSuccess(true);
            toast.success(`Meeting confirmed with ${entityName}.`);
            setTimeout(() => navigate('/dashboard/meetings'), 2200);
        } catch (error) {
            console.error('Error booking meeting:', error);
            toast.error(friendlyError(error, 'The meeting could not be booked. The recipient may have removed calendar access or the slot may no longer be available.'));
        } finally {
            setScheduling(false);
        }
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box className="erp-page-shell erp-internal-page tf-schedule-page" sx={{
            minHeight: '100vh',
            position: 'relative',
            bgcolor: palette.canvas,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            overflow: 'hidden',
        }}>

            <style>{`
                @keyframes shimmerEdge { 0% { background-position: 0% 0%; } 100% { background-position: 300% 0%; } }
                @keyframes pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.05); opacity: 0.8; } }
                @keyframes tfFloat {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }
            `}</style>

            <Header />

            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    py: { xs: 3, md: 4 },
                    px: { xs: 2, sm: 3, md: 4, lg: 6 },
                    position: 'relative',
                    width: '100%',
                    zIndex: 1,
                }}
            >
                {/* PAGE HEADER */}
                <Box className="erp-page-hero" sx={{ mb: 2.5, p: { xs: 2, md: 2.25 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ ...glassPod(palette.rust, 56, '16px'), animation: 'tfFloat 3s ease-in-out infinite' }}>
                                    <SignalIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: { xs: '1.35rem', md: '1.5rem' }, color: palette.ink, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                                        Schedule a Meeting
                                    </Typography>
                                    <Typography sx={{ color: palette.inkSoft, mt: 0.5, fontSize: '0.9rem' }}>
                                        Book only with recipients who explicitly shared their calendar
                                    </Typography>
                                    <SectionRule />
                                </Box>
                            </Box>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                        >
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/dashboard/meetings')}
                                sx={{ ...glassGhostButton(palette.ink), px: 3, py: 1 }}
                            >
                                Back to Meetings
                            </Button>
                        </motion.div>
                    </Box>
                </Box>

                {/* COMMAND CENTER WORKFLOW CARDS */}
                <Box className="tf-schedule-command-grid">
                    {[
                        {
                            color: '#8B4DE8',
                            icon: <HandshakeIcon />,
                            title: 'Recipient-Controlled Booking',
                            eyebrow: 'PERMISSION FIRST',
                            text: 'A meeting becomes bookable only after the recipient explicitly shares their calendar.',
                            metric: `${Object.values(userConnectionStatuses).filter(s => s === 'calendar_shared').length} shared calendars`,
                            art: <ConnectIcon />,
                        },
                        {
                            color: '#2F6FED',
                            icon: <CalendarIcon />,
                            title: 'Calendar Availability',
                            eyebrow: 'LIVE AVAILABILITY',
                            text: 'TELCOFAIR combines shared slots with existing meetings and connected-calendar busy time.',
                            metric: selectedEntity ? (selectedEntity.displayName || selectedEntity.companyName || 'Recipient selected') : 'Select a recipient',
                            art: <EventIcon />,
                        },
                        {
                            color: '#13A773',
                            icon: <CheckCircleIcon />,
                            title: 'Conflict-Safe Booking',
                            eyebrow: 'SMART SCHEDULING',
                            text: 'External busy periods are checked again before confirmation to reduce double bookings.',
                            metric: 'Conflict checks active',
                            art: <ScheduleIcon />,
                        },
                    ].map((card) => (
                        <Paper key={card.title} elevation={0} className="tf-schedule-command-card" sx={{ '--schedule-color': card.color }}>
                            <Box className="tf-schedule-command-art">{card.art}</Box>
                            <Stack direction="row" spacing={1.05} sx={{ alignItems: 'center' }}>
                                <Box className="tf-schedule-command-icon">{card.icon}</Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 7.8, fontWeight: 950, letterSpacing: '.09em', color: card.color }}>{card.eyebrow}</Typography>
                                    <Typography sx={{ mt: .15, fontSize: 11.8, fontWeight: 950, color: '#223249' }}>{card.title}</Typography>
                                </Box>
                            </Stack>
                            <Typography sx={{ mt: .8, fontSize: 9.5, lineHeight: 1.5, color: '#6E7E91', fontWeight: 650 }}>{card.text}</Typography>
                            <Chip size="small" label={card.metric} sx={{ mt: .9, height: 22, bgcolor: alpha(card.color, .075), color: card.color, border: `1px solid ${alpha(card.color,.12)}`, fontSize: 8.5, fontWeight: 900 }} />
                        </Paper>
                    ))}
                </Box>

                {/* STEP INDICATOR */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                >
                    <GlassStepIndicator activeStep={activeStep} totalSteps={3} />
                </motion.div>

                {/* MAIN GLASS CARD */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Paper elevation={0} className="tf-schedule-builder" sx={{ ...glassCard({ level: 3, radius: '18px', accent: palette.rust }), p: { xs: 2, md: 2.5 } }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: '8%',
                            right: '8%',
                            height: '2px',
                            borderRadius: '0 0 999px 999px',
                            background: `linear-gradient(90deg, transparent, ${alpha(palette.rust, 0.6)}, ${alpha(palette.gold, 0.4)}, ${alpha(palette.pine, 0.4)}, transparent)`,
                            backgroundSize: '300% 100%',
                            animation: 'shimmerEdge 8s linear infinite',
                            pointerEvents: 'none',
                            zIndex: 2,
                        }} />

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeStep}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {/* ======================== STEP 0 ======================== */}
                                {activeStep === 0 && (
                                    <Box>
                                        <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 0.5, fontSize: '1.1rem', color: palette.ink }}>
                                            Select a {tabValue === 0 ? 'Company' : 'User'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: palette.inkSoft, mb: 1.25 }}>
                                            {tabValue === 0 ? 'Browse companies, then choose a person from that company.' : 'Choose a person. Calendar-shared contacts can be booked immediately; other contacts remain visible for context.'}
                                        </Typography>

                                        {/* Tabs */}
                                        <Box sx={{ mb: 1.25 }}>
                                            <Tabs
                                                value={tabValue}
                                                onChange={handleTabChange}
                                                sx={{
                                                    '& .MuiTabs-flexContainer': { gap: 1 },
                                                    '& .MuiTab-root': {
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        borderRadius: '10px',
                                                        minHeight: 36,
                                                        px: 2,
                                                        fontSize: '0.8rem',
                                                        color: palette.inkSoft,
                                                        '&.Mui-selected': {
                                                            color: getGradientContrastText(palette.rust, palette.rustDeep),
                                                            background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                                            boxShadow: `0 4px 16px ${alpha(palette.rust, 0.35)}`,
                                                        }
                                                    },
                                                    '& .MuiTabs-indicator': { display: 'none' },
                                                }}
                                            >
                                                <Tab 
                                                    icon={<BusinessIcon sx={{ fontSize: 18 }} />} 
                                                    label={`Companies (${allCompanies.length})`} 
                                                />
                                                <Tab 
                                                    icon={<PeopleIcon sx={{ fontSize: 18 }} />} 
                                                    label={`People (${allUsers.length})`} 
                                                />
                                            </Tabs>
                                        </Box>

                                        {/* Search & Filter */}
                                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.25, p: 1, ...glassWell('14px') }}>
                                            <TextField
                                                placeholder={`Search ${tabValue === 0 ? 'companies' : 'users'} by name, service, interest...`}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                size="small"
                                                sx={{ flex: 1, minWidth: '180px' }}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon sx={{ color: palette.inkFaint, fontSize: 18 }} />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment: searchTerm ? (
                                                            <InputAdornment position="end">
                                                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ) : null,
                                                        sx: { borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.4)', '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' } }
                                                    }
                                                }}
                                            />

                                            <FormControl size="small" sx={{ minWidth: '160px' }}>
                                                <InputLabel sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.8rem' }}>Connection</InputLabel>
                                                <Select
                                                    value={connectionFilter}
                                                    onChange={(e) => setConnectionFilter(e.target.value)}
                                                    label="Connection"
                                                    sx={{ borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.4)', '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' }, fontSize: '0.8rem' }}
                                                >
                                                    <MenuItem value="all">All Connections</MenuItem>
                                                    <MenuItem value="connected">Connected</MenuItem>
                                                    <MenuItem value="pending">Pending</MenuItem>
                                                </Select>
                                            </FormControl>

                                            <Button
                                                startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                                                onClick={() => { fetchCompanies(true); fetchUsers(); }}
                                                disabled={loading}
                                                size="small"
                                                sx={{ ...glassGhostButton(palette.rust), px: 2, py: 0.8, fontSize: '0.72rem', height: 40 }}
                                            >
                                                Refresh
                                            </Button>
                                        </Box>

                                        {/* Stats chips */}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
                                            <Box sx={{ ...glassPill(palette.ink), px: 1.2, height: 24, fontSize: '0.6rem' }}>
                                                {tabValue === 0 ? <BusinessIcon sx={{ fontSize: 12 }} /> : <PeopleIcon sx={{ fontSize: 12 }} />}
                                                {tabValue === 0 ? allCompanies.length : allUsers.length} {tabValue === 0 ? 'Companies' : 'Users'}
                                            </Box>
                                            <Box sx={{ ...glassPill(palette.pine), px: 1.2, height: 24, fontSize: '0.6rem' }}>
                                                <CheckCircleIcon sx={{ fontSize: 12 }} />
                                                {tabValue === 0 ? Object.values(connectionStatuses).filter(s => s === 'connected').length : Object.values(userConnectionStatuses).filter(s => s === 'calendar_shared').length} {tabValue === 0 ? 'Connected' : 'Calendar Shared'}
                                            </Box>
                                            <Box sx={{ ...glassPill(palette.gold), px: 1.2, height: 24, fontSize: '0.6rem' }}>
                                                <ScheduleIcon sx={{ fontSize: 12 }} />
                                                {Object.values(tabValue === 0 ? connectionStatuses : userConnectionStatuses).filter(s => s === 'pending').length} Pending
                                            </Box>
                                            {searchTerm && (
                                                <Box sx={{ ...glassPill(palette.rust), px: 1.2, height: 24, fontSize: '0.6rem' }}>
                                                    {tabValue === 0 ? filteredCompanies.length : filteredUsers.length} results
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Selected Entity Banner */}
                                        <AnimatePresence>
                                            {selectedEntity && (
                                                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                                                    <Alert
                                                        onClose={() => { setSelectedEntity(null); setSelectedEntityType(null); }}
                                                        icon={<CheckCircleIcon />}
                                                        sx={{
                                                            mb: 1.25,
                                                            borderRadius: '14px',
                                                            ...glassWell('14px', palette.rust),
                                                            '& .MuiAlert-icon': { color: palette.rust },
                                                            '& .MuiAlert-message': { width: '100%' }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Box sx={{ position:'relative', width:34, height:38, flexShrink:0 }}>
                                                                <Avatar
                                                                    src={(selectedEntityType === 'user' ? getPersonPhotoUrl(selectedEntity) : getCompanyLogoUrl(selectedEntity)) || undefined}
                                                                    sx={{ width:32, height:32, borderRadius:selectedEntityType === 'user' ? '50%' : '9px', ...glassPod(palette.rust, 32, selectedEntityType === 'user' ? '50%' : '9px'), '& img':{ objectFit:selectedEntityType === 'user' ? 'cover' : 'contain', padding:selectedEntityType === 'user' ? 0 : '2px' } }}
                                                                >
                                                                    {(selectedEntity.companyName || selectedEntity.displayName || selectedEntity.name || 'E').charAt(0)}
                                                                </Avatar>
                                                                {selectedEntityType === 'user' && (() => {
                                                                    const selectedCompany = allCompanies.find((company) => String(company.id || company.companyId || '') === String(selectedEntity.companyId || selectedEntity.company?.id || selectedEntity.company?.companyId || ''));
                                                                    const selectedCompanyLogo = getCompanyLogoUrl(selectedCompany || {});
                                                                    return selectedCompanyLogo ? <Avatar src={selectedCompanyLogo} sx={{ position:'absolute', left:'50%', transform:'translateX(-50%)', bottom:0, width:16, height:16, bgcolor:'#fff', border:'1.5px solid #fff', '& img':{ objectFit:'contain', padding:'1px' } }} /> : null;
                                                                })()}
                                                            </Box>
                                                            <Typography variant="body2" fontWeight={600} sx={{ color: palette.rust, fontSize: '0.8rem' }}>
                                                                Selected: {selectedEntity.companyName || selectedEntity.displayName || 'Unknown'}
                                                                {selectedEntityType === 'user' && ' (User)'}
                                                                {selectedEntity.specialization && ` • ${selectedEntity.specialization}`}
                                                                {selectedEntity.connectionStatus === 'pending' && ' (Pending)'}
                                                            </Typography>
                                                            {formData.isAIIntro && (
                                                                <Box sx={{ ...glassPill(palette.purple, true), px: 0.8, height: 20, fontSize: '0.5rem' }}>
                                                                    <AiIcon sx={{ fontSize: 10 }} />
                                                                    AI Intro
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Alert>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        {/* Entity Grid - 2 columns */}
                                        {loading ? (
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                                                {[...Array(6)].map((_, i) => (
                                                    <GlassSkeletonGridCard key={i} />
                                                ))}
                                            </Box>
                                        ) : tabValue === 0 ? (
                                            // Companies Grid
                                            filteredCompanies.length === 0 ? (
                                                <Box sx={{ textAlign: 'center', py: 6, ...glassCard({ level: 1, radius: '16px' }) }}>
                                                    <Box sx={{ ...glassPod(palette.inkFaint, 72, '50%'), mx: 'auto', mb: 2 }}>
                                                        <BusinessIcon sx={{ fontSize: 36, opacity: 0.5 }} />
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: palette.ink, mt: 1.5, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '0.82rem' }}>
                                                        No companies found
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 0.5, fontSize: '0.8rem' }}>
                                                        {searchTerm ? 'Try adjusting your search' : 'No company records are available to this account.'}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                                                    {filteredCompanies.map((company, index) => (
                                                        <GlassGridCompanyCard
                                                            key={company.id}
                                                            company={company}
                                                            isSelected={selectedEntity?.id === company.id && selectedEntityType === 'company'}
                                                            onSelect={(c) => handleEntitySelect(c, 'company')}
                                                            connectionStatus={connectionStatuses[company.id] || 'not_connected'}
                                                            index={index}
                                                        />
                                                    ))}
                                                </Box>
                                            )
                                        ) : (
                                            // Users Grid with complete info
                                            filteredUsers.length === 0 ? (
                                                <Box sx={{ textAlign: 'center', py: 6, ...glassCard({ level: 1, radius: '16px' }) }}>
                                                    <Box sx={{ ...glassPod(palette.inkFaint, 72, '50%'), mx: 'auto', mb: 2 }}>
                                                        <PeopleIcon sx={{ fontSize: 36, opacity: 0.5 }} />
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: palette.ink, mt: 1.5, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '0.82rem' }}>
                                                        No people found
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 0.5, fontSize: '0.8rem' }}>
                                                        {searchTerm ? 'Try adjusting your search' : selectedCompanyScopeId ? 'No eligible people are registered for this company.' : 'No people records are available to this account.'}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                                                    {filteredUsers.map((user, index) => (
                                                        <GlassUserCard
                                                            key={user.id}
                                                            user={user}
                                                            company={allCompanies.find((company) => String(company.id || company.companyId || '') === String(user.companyId || user.company?.id || user.company?.companyId || '')) || null}
                                                            isSelected={selectedEntity?.id === user.id && selectedEntityType === 'user'}
                                                            onSelect={(u) => handleEntitySelect(u, 'user')}
                                                            connectionStatus={userConnectionStatuses[user.uid || user.id] || 'not_connected'}
                                                            index={index}
                                                        />
                                                    ))}
                                                </Box>
                                            )
                                        )}

                                        {/* Load More */}
                                        {tabValue === 0 && hasMore && !loading && allCompanies.length > 0 && !searchTerm && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                                                <Button
                                                    onClick={() => fetchCompanies(false)}
                                                    disabled={loadingMore}
                                                    startIcon={loadingMore ? <CircularProgress size={14} /> : null}
                                                    sx={{ ...glassGhostButton(palette.ink), px: 3, py: 0.8 }}
                                                >
                                                    {loadingMore ? 'Loading…' : 'Load More'}
                                                </Button>
                                            </Box>
                                        )}

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3.5 }}>
                                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleNext}
                                                    disabled={!selectedEntity || loadingSlots || requestingAvailability}
                                                    endIcon={(loadingSlots || requestingAvailability) ? <CircularProgress size={15} color="inherit" /> : <ArrowForwardIcon />}
                                                    sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 4, py: 1.2 }}
                                                >
                                                    {requestingAvailability ? 'Sending request…' : loadingSlots ? 'Checking availability…' : (selectedEntityType === 'user' && userConnectionStatuses[selectedEntity?.uid || selectedEntity?.userId || selectedEntity?.id] !== 'calendar_shared' ? 'Request availability' : 'Continue')}
                                                </Button>
                                            </motion.div>
                                        </Box>
                                    </Box>
                                )}

                                {/* ======================== STEP 1 ======================== */}
                                {activeStep === 1 && (
                                    <Box>
                                        <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 0.5, fontSize: '1.1rem', color: palette.ink }}>
                                            Choose a Time Slot
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: palette.inkSoft, mb: 1.25 }}>
                                            Select an available time for your meeting with <strong>{selectedEntity?.companyName || selectedEntity?.displayName || 'the selected entity'}</strong>
                                        </Typography>

                                        {/* Selected entity summary */}
                                        <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '14px' }), p: 2, mb: 1.25 }}>
                                            <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                                                <Avatar sx={{ ...glassPod(palette.rust, 44, '12px'), fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '0.82rem' }}>
                                                    {(selectedEntity?.companyName || selectedEntity?.displayName || 'E').charAt(0)}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.85rem' }}>
                                                        {selectedEntity?.companyName || selectedEntity?.displayName || 'Unknown'}
                                                        {selectedEntityType === 'user' && ' (User)'}
                                                        {selectedEntity?.specialization && ` • ${selectedEntity.specialization}`}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                                        {selectedEntity?.country || selectedEntity?.region || selectedEntity?.companyName || selectedEntity?.email || 'N/A'}
                                                        {selectedEntityType === 'user' && ` • ${selectedEntity?.designation || 'Team Member'}`}
                                                        {selectedEntity?.primaryService && ` • ${selectedEntity.primaryService}`}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ ...glassPill(
                                                    (tabValue === 0 ? connectionStatuses[selectedEntity?.id] : userConnectionStatuses[selectedEntity?.uid || selectedEntity?.id]) === 'connected' 
                                                        ? palette.pine : palette.gold
                                                ), px: 1.2, height: 22, fontSize: '0.55rem' }}>
                                                    {(tabValue === 0 ? connectionStatuses[selectedEntity?.id] : userConnectionStatuses[selectedEntity?.uid || selectedEntity?.id]) === 'connected' 
                                                        ? 'Connected' : 'Pending'}
                                                </Box>
                                            </Box>
                                            {selectedEntity?.interests && selectedEntity.interests.length > 0 && (
                                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    <Typography sx={{ fontSize: '0.5rem', color: palette.inkFaint, fontWeight: 600, mr: 0.5 }}>
                                                        Interested in:
                                                    </Typography>
                                                    {selectedEntity.interests.slice(0, 3).map((interest, idx) => (
                                                        <Box key={idx} sx={{ ...glassPill(palette.purple), px: 0.6, height: 16, fontSize: '0.4rem' }}>
                                                            {interest}
                                                        </Box>
                                                    ))}
                                                    {selectedEntity.interests.length > 3 && (
                                                        <Box sx={{ ...glassPill(palette.inkFaint), px: 0.6, height: 16, fontSize: '0.4rem' }}>
                                                            +{selectedEntity.interests.length - 3}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Paper>

                                        {/* Date + Duration */}
                                        <Grid container spacing={2.5}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DatePicker
                                                        label="Meeting Date"
                                                        value={formData.date}
                                                        onChange={(newDate) => { if (newDate) setFormData(prev => ({ ...prev, date: newDate })); }}
                                                        minDate={new Date()}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                size: 'small',
                                                                sx: {
                                                                    '& .MuiOutlinedInput-root': {
                                                                        ...glassWell('12px'),
                                                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                                                                        '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.7)' }
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.8rem' }}>Duration</InputLabel>
                                                    <Select
                                                        value={formData.duration}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                                                        label="Duration"
                                                        sx={{ ...glassWell('12px'), fontSize: '0.8rem' }}
                                                    >
                                                        <MenuItem value={15}>15 minutes</MenuItem>
                                                        <MenuItem value={30}>30 minutes</MenuItem>
                                                        <MenuItem value={45}>45 minutes</MenuItem>
                                                        <MenuItem value={60}>1 hour</MenuItem>
                                                        <MenuItem value={90}>1.5 hours</MenuItem>
                                                        <MenuItem value={120}>2 hours</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>

                                        {/* Time Slots */}
                                        <Box sx={{ mt: 2.5 }}>
                                            <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink, mb: 0.8, fontSize: '0.8rem' }}>
                                                Available Time Slots
                                            </Typography>

                                            {availableSlots.length === 0 ? (
                                                <Box sx={{ textAlign: 'center', py: 3, ...glassWell('12px') }}>
                                                    <CircularProgress size={24} sx={{ color: palette.rust }} />
                                                    <Typography sx={{ color: palette.inkSoft, mt: 1, fontSize: '0.8rem' }}>Loading time slots…</Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                    {availableSlots.map((slot, index) => (
                                                        <motion.div key={index} whileHover={{ scale: slot.available ? 1.04 : 1 }} whileTap={{ scale: slot.available ? 0.96 : 1 }}>
                                                            <Button
                                                                variant={slot.selected ? 'contained' : 'outlined'}
                                                                disabled={!slot.available}
                                                                onClick={() => handleSlotSelect(index)}
                                                                size="small"
                                                                sx={{
                                                                    borderRadius: '10px',
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.7rem',
                                                                    minWidth: '80px',
                                                                    py: 0.75,
                                                                    ...(slot.selected
                                                                        ? glassButton(palette.rust, palette.rustDeep)
                                                                        : {
                                                                            borderColor: slot.available ? alpha(palette.ink, 0.1) : alpha(palette.ink, 0.04),
                                                                            color: slot.available ? palette.ink : alpha(palette.inkSoft, 0.3),
                                                                            bgcolor: 'rgba(255,255,255,0.2)',
                                                                            '&:hover': slot.available ? {
                                                                                borderColor: palette.rust,
                                                                                bgcolor: alpha(palette.rust, 0.04)
                                                                            } : {}
                                                                        })
                                                                }}
                                                            >
                                                                {slot.display}
                                                            </Button>
                                                        </motion.div>
                                                    ))}
                                                </Box>
                                            )}

                                            {selectedSlot && (
                                                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                                                    <Alert
                                                        icon={<CheckCircleIcon />}
                                                        sx={{
                                                            mt: 2,
                                                            borderRadius: '14px',
                                                            ...glassWell('14px', palette.pine),
                                                            '& .MuiAlert-icon': { color: palette.pine },
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={600} sx={{ color: palette.pine, fontSize: '0.8rem' }}>
                                                            Selected: {selectedSlot.display} on {formData.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                                        </Typography>
                                                    </Alert>
                                                </motion.div>
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3.5 }}>
                                            <Button onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ ...glassGhostButton(palette.ink), px: 3, py: 1 }}>
                                                Back
                                            </Button>
                                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleNext}
                                                    disabled={!selectedSlot}
                                                    endIcon={<ArrowForwardIcon />}
                                                    sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 4, py: 1.2 }}
                                                >
                                                    Review & Send
                                                </Button>
                                            </motion.div>
                                        </Box>
                                    </Box>
                                )}

                                {/* ======================== STEP 2 ======================== */}
                                {activeStep === 2 && (
                                    <Box>
                                        <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 0.5, fontSize: '1.1rem', color: palette.ink }}>
                                            Review & Send Request
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: palette.inkSoft, mb: 1.25 }}>
                                            Review the meeting details before sending the request
                                        </Typography>

                                        {/* Summary Card */}
                                        <Paper elevation={0} sx={{ ...glassCard({ level: 2, radius: '16px', accent: palette.rust }), p: 2.5, mb: 1.25 }}>
                                            <Grid container spacing={2.5}>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar sx={{ ...glassPod(palette.rust, 52, '14px'), fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.1rem' }}>
                                                            {(selectedEntity?.companyName || selectedEntity?.displayName || 'E').charAt(0)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.9rem' }}>
                                                                {selectedEntity?.companyName || selectedEntity?.displayName || 'Unknown'}
                                                                {selectedEntityType === 'user' && ' (User)'}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                                                {selectedEntity?.country || selectedEntity?.region || selectedEntity?.companyName || selectedEntity?.email || 'N/A'}
                                                                {selectedEntity?.specialization && ` • ${selectedEntity.specialization}`}
                                                            </Typography>
                                                            {(tabValue === 0 ? connectionStatuses[selectedEntity?.id] : userConnectionStatuses[selectedEntity?.uid || selectedEntity?.id]) === 'connected' && (
                                                                <Box sx={{ mt: 0.25 }}>
                                                                    <Box sx={{ ...glassPill(palette.pine), px: 0.8, height: 18, fontSize: '0.5rem' }}>
                                                                        <CheckCircleIcon sx={{ fontSize: 10 }} />
                                                                        Connected
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        {[
                                                            { icon: <CalendarIcon sx={{ fontSize: 16 }} />, color: palette.teal, text: formData.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) },
                                                            { icon: <TimeIcon sx={{ fontSize: 16 }} />, color: palette.gold, text: `${selectedSlot?.display} • ${formData.duration} min` },
                                                            { icon: <PeopleIcon sx={{ fontSize: 16 }} />, color: palette.pine, text: '2 participants' },
                                                            { icon: <VideoCallIcon sx={{ fontSize: 16 }} />, color: palette.purple, text: formData.type === 'video' ? 'Video Call' : formData.type === 'in-person' ? 'In-Person' : 'Phone Call' },
                                                        ].map((item, i) => (
                                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Box sx={{ ...glassPod(item.color, 30, '8px') }}>
                                                                    {item.icon}
                                                                </Box>
                                                                <Typography variant="body2" fontWeight={500} sx={{ color: palette.ink, fontSize: '0.8rem' }}>{item.text}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Paper>

                                        {/* ChatGPT meeting-message toggle */}
                                        <Box sx={{ mb: 1.25, p: 1, ...glassWell('14px') }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.isAIIntro}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({ 
                                                                ...prev, 
                                                                isAIIntro: e.target.checked,
                                                                message: e.target.checked ? prev.aiIntroMessage : '',
                                                            }));
                                                            if (e.target.checked && !formData.aiIntroMessage) {
                                                                generateAIIntro();
                                                            }
                                                        }}
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: palette.purple,
                                                                '&:hover': { bgcolor: alpha(palette.purple, 0.08) },
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                bgcolor: palette.purple,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AiIcon sx={{ color: formData.isAIIntro ? palette.purple : palette.inkFaint }} />
                                                        <Typography variant="body2" fontWeight={600} sx={{ color: formData.isAIIntro ? palette.purple : palette.inkSoft }}>
                                                            Use ChatGPT Meeting Message
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </Box>

                                        {/* Form Fields */}
                                        <Grid container spacing={2.5}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Meeting Title"
                                                    value={formData.title}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                                    placeholder={`Meeting with ${selectedEntity?.companyName || selectedEntity?.displayName || 'the selected entity'}`}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            ...glassWell('12px'),
                                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                                                            '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.7)' }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.8rem' }}>Meeting Type</InputLabel>
                                                    <Select
                                                        value={formData.type}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                                                        label="Meeting Type"
                                                        sx={{ ...glassWell('12px'), fontSize: '0.8rem' }}
                                                    >
                                                        <MenuItem value="video"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><VideoCallIcon sx={{ fontSize: 16 }} /> Video Call</Box></MenuItem>
                                                        <MenuItem value="in-person"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocationIcon sx={{ fontSize: 16 }} /> In-Person</Box></MenuItem>
                                                        <MenuItem value="phone"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneIcon sx={{ fontSize: 16 }} /> Phone Call</Box></MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Agenda"
                                                    value={formData.agenda}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, agenda: e.target.value }))}
                                                    multiline
                                                    rows={2}
                                                    placeholder="What would you like to discuss?"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            ...glassWell('12px'),
                                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                                                            '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.7)' }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <TextField
                                                    fullWidth
                                                    label={formData.isAIIntro ? "ChatGPT Meeting Message" : "Message (Optional)"}
                                                    value={formData.isAIIntro ? formData.aiIntroMessage : formData.message}
                                                    onChange={(e) => {
                                                        if (formData.isAIIntro) {
                                                            setFormData(prev => ({ ...prev, aiIntroMessage: e.target.value, message: e.target.value }));
                                                        } else {
                                                            setFormData(prev => ({ ...prev, message: e.target.value }));
                                                        }
                                                    }}
                                                    multiline
                                                    rows={3}
                                                    placeholder={formData.isAIIntro ? "ChatGPT-generated meeting message..." : "Add a personal message…"}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            ...glassWell('12px'),
                                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                                                            '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.7)' }
                                                        }
                                                    }}
                                                    slotProps={{
                                                        input: formData.isAIIntro ? {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Box sx={{ ...glassPod(palette.purple, 28, '8px') }}>
                                                                        <AiIcon sx={{ fontSize: 16 }} />
                                                                    </Box>
                                                                </InputAdornment>
                                                            ),
                                                        } : {}
                                                    }}
                                                />
                                                {formData.isAIIntro && (
                                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                                        <Button
                                                            size="small"
                                                            onClick={generateAIIntro}
                                                            disabled={generatingAI}
                                                            startIcon={generatingAI ? <CircularProgress size={14} /> : <RefreshIcon />}
                                                            sx={{ ...glassGhostButton(palette.purple), fontSize: '0.7rem' }}
                                                        >
                                                            {generatingAI ? 'Generating...' : 'Regenerate with ChatGPT'}
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Grid>
                                        </Grid>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3.5 }}>
                                            <Button onClick={handleBack} startIcon={<ArrowBackIcon />} disabled={scheduling} sx={{ ...glassGhostButton(palette.ink), px: 3, py: 1 }}>
                                                Back
                                            </Button>
                                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSchedule}
                                                    disabled={scheduling}
                                                    startIcon={scheduling ? <CircularProgress size={16} color="inherit" /> : formData.isAIIntro ? <AiIcon /> : <SendIcon />}
                                                    sx={{ ...glassButton(formData.isAIIntro ? palette.purple : palette.pine, formData.isAIIntro ? palette.purpleLight : palette.pineDeep), px: 4, py: 1.2 }}
                                                >
                                                    {scheduling ? 'Confirming…' : 'Confirm Meeting'}
                                                </Button>
                                            </motion.div>
                                        </Box>
                                    </Box>
                                )}

                            </motion.div>
                        </AnimatePresence>
                    </Paper>
                </motion.div>
            </Container>

            {/* Success Dialog */}
            <Dialog
                open={showSuccess}
                onClose={() => {}}
                maxWidth="sm"
                fullWidth
                
                slotProps={{ backdrop: {
                    sx: { backdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) }
                }, ...({ paper: {
                    sx: {
                        ...glassCard({ level: 4, radius: '28px', accent: palette.pine }),
                        overflow: 'hidden',
                    }
                } }) }}
            >
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                        <Box sx={{ ...glassPod(formData.isAIIntro ? palette.purple : palette.pine, 80, '50%'), mx: 'auto', mb: 2 }}>
                            {formData.isAIIntro ? <AiIcon sx={{ fontSize: 40 }} /> : <CheckCircleIcon sx={{ fontSize: 40 }} />}
                        </Box>
                    </motion.div>
                    <Typography variant="h5" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, color: palette.ink, mb: 1 }}>
                        Meeting Confirmed!
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.inkSoft, mb: 0.5 }}>
                        {`Your meeting with ${selectedEntity?.companyName || selectedEntity?.displayName || 'the recipient'} is confirmed for the selected calendar slot.`}
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.inkFaint, fontSize: '0.75rem' }}>
                        Both calendars and the TELCOFAIR meeting history have been updated
                    </Typography>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/dashboard/meetings')}
                            sx={{ ...glassGhostButton(palette.ink), px: 3 }}
                        >
                            View Meetings
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/dashboard')}
                            sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 3 }}
                        >
                            Go to Dashboard
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default ScheduleMeeting;